import { useSelector } from 'react-redux';

export const useManagerPermissions = () => {
    const auth = useSelector(state => state.auth);
    const user = useSelector(state => state.user);
    const warehouse = useSelector(state => state.warehouse);

    const canManagerEditMaterial = (material) => {
        // If not authenticated, return false
        if (!auth.user) return false;

        // Check if user has the basic permission first
        const hasEditPermission = auth?.permissions?.all_permissions?.includes('edit materials');
        if (!hasEditPermission) return false;

        // If user is superadmin, always allow
        if (auth.user.role_name === 'superadmin') return true;

        // If user is not a manager, use standard permission
        if (auth.user.role_name !== 'manager') return true;

        // For managers, check if they manage the firm that owns this material
        if (auth.user.role_name === 'manager') {
            // Get the list of firms this manager can work with
            const managerFirms = user.firm_options || [];
            
            // Check if the material's firm is in the manager's allowed firms
            const materialFirmId = material?.firm?.id;
            if (!materialFirmId) return false; // No firm associated with material
            
            return managerFirms.some(firm => firm.id === materialFirmId);
        }

        return false;
    };

    const canManagerDeleteMaterial = (material) => {
        // Similar logic for delete permissions
        if (!auth.user) return false;

        const hasDeletePermission = auth?.permissions?.all_permissions?.includes('delete materials');
        if (!hasDeletePermission) return false;

        if (auth.user.role_name === 'superadmin') return true;
        if (auth.user.role_name !== 'manager') return true;

        if (auth.user.role_name === 'manager') {
            const managerFirms = user.firm_options || [];
            const materialFirmId = material?.firm?.id;
            if (!materialFirmId) return false;
            
            return managerFirms.some(firm => firm.id === materialFirmId);
        }

        return false;
    };

    const canManagerEditProduct = (product) => {
        // Similar logic for product edit permissions
        if (!auth.user) return false;

        const hasEditPermission = auth?.permissions?.all_permissions?.includes('edit products');
        if (!hasEditPermission) return false;

        if (auth.user.role_name === 'superadmin') return true;
        if (auth.user.role_name !== 'manager') return true;

        if (auth.user.role_name === 'manager') {
            const managerFirms = user.firm_options || [];
            const productFirmId = product?.firm?.id;
            if (!productFirmId) return false;
            
            return managerFirms.some(firm => firm.id === productFirmId);
        }

        return false;
    };

    const canManagerDeleteProduct = (product) => {
        // Similar logic for product delete permissions
        if (!auth.user) return false;

        const hasDeletePermission = auth?.permissions?.all_permissions?.includes('delete products');
        if (!hasDeletePermission) return false;

        if (auth.user.role_name === 'superadmin') return true;
        if (auth.user.role_name !== 'manager') return true;

        if (auth.user.role_name === 'manager') {
            const managerFirms = user.firm_options || [];
            const productFirmId = product?.firm?.id;
            if (!productFirmId) return false;
            
            return managerFirms.some(firm => firm.id === productFirmId);
        }

        return false;
    };

    const canManagerEditMaterialImport = (materialImport) => {
        // Material import permission logic
        if (!auth.user) return false;

        // Directors and superadmins can edit ANY import regardless of permissions or time restrictions
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;

        // For other roles, check specific permissions first
        const hasEditPermission = auth?.permissions?.all_permissions?.includes('edit material imports');
        if (!hasEditPermission) return false;

        // For other roles, check the backend can_edit flag (time-based restrictions)
        if (!materialImport?.can_edit) return false;

        if (auth.user.role_name !== 'manager') return true;

        if (auth.user.role_name === 'manager') {
            const managerFirms = user.firm_options || [];
            // Material import -> warehouse -> firm
            const importFirmId = materialImport?.warehouse?.firm_id || materialImport?.warehouse?.firm?.id;
            if (!importFirmId) return false;
            
            return managerFirms.some(firm => firm.id === importFirmId);
        }

        return false;
    };

    const canManagerDeleteMaterialImport = (materialImport) => {
        // Material import delete permission logic
        if (!auth.user) return false;

        // Directors and superadmins can delete ANY import regardless of permissions or time restrictions
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;

        // For other roles, check specific permissions first
        const hasDeletePermission = auth?.permissions?.all_permissions?.includes('delete material imports');
        if (!hasDeletePermission) return false;

        // For other roles, check the backend can_edit flag (time-based restrictions)
        if (!materialImport?.can_edit) return false;

        if (auth.user.role_name !== 'manager') return true;

        if (auth.user.role_name === 'manager') {
            const managerFirms = user.firm_options || [];
            // Material import -> warehouse -> firm
            const importFirmId = materialImport?.warehouse?.firm_id || materialImport?.warehouse?.firm?.id;
            if (!importFirmId) return false;
            
            return managerFirms.some(firm => firm.id === importFirmId);
        }

        return false;
    };

    const canManagerEditProduction = (production) => {
        // Production permission logic
        if (!auth.user) return false;

        // Directors and superadmins can edit ANY production regardless of permissions or time restrictions
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;

        // For other roles, check specific permissions first
        const hasEditPermission = auth?.permissions?.all_permissions?.includes('edit productions');
        if (!hasEditPermission) return false;

        // For other roles, check the backend can_edit flag (time-based restrictions)
        if (!production?.can_edit) return false;

        if (auth.user.role_name !== 'manager') return true;

        if (auth.user.role_name === 'manager') {
            const managerFirms = user.firm_options || [];
            // Production -> warehouse -> firm
            const productionFirmId = production?.warehouse?.firm_id || production?.warehouse?.firm?.id;
            if (!productionFirmId) return false;
            
            return managerFirms.some(firm => firm.id === productionFirmId);
        }

        return false;
    };

    const canManagerDeleteProduction = (production) => {
        // Production delete permission logic
        if (!auth.user) return false;

        // Directors and superadmins can delete ANY production regardless of permissions or time restrictions
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;

        // For other roles, check specific permissions first
        const hasDeletePermission = auth?.permissions?.all_permissions?.includes('delete productions');
        if (!hasDeletePermission) return false;

        // For other roles, check the backend can_edit flag (time-based restrictions)
        if (!production?.can_edit) return false;

        if (auth.user.role_name !== 'manager') return true;

        if (auth.user.role_name === 'manager') {
            const managerFirms = user.firm_options || [];
            // Production -> warehouse -> firm
            const productionFirmId = production?.warehouse?.firm_id || production?.warehouse?.firm?.id;
            if (!productionFirmId) return false;
            
            return managerFirms.some(firm => firm.id === productionFirmId);
        }

        return false;
    };

    const canManagerEditSale = (sale) => {
        // Sale permission logic
        if (!auth.user) return false;

        // Directors and superadmins can edit ANY sale regardless of permissions or time restrictions
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;

        // For other roles, check specific permissions first
        const hasEditPermission = auth?.permissions?.all_permissions?.includes('edit sales');
        if (!hasEditPermission) return false;

        // For other roles, check the backend can_edit flag (time-based restrictions)
        if (!sale?.can_edit) return false;

        if (auth.user.role_name !== 'manager') return true;

        if (auth.user.role_name === 'manager') {
            const managerFirms = user.firm_options || [];
            // Sale -> firm_id
            const saleFirmId = sale?.firm_id;
            if (!saleFirmId) return false;
            
            return managerFirms.some(firm => firm.id === saleFirmId);
        }

        return false;
    };

    const canManagerDeleteSale = (sale) => {
        // Sale delete permission logic
        if (!auth.user) return false;

        // Directors and superadmins can delete ANY sale regardless of permissions or time restrictions
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;

        // For other roles, check specific permissions first
        const hasDeletePermission = auth?.permissions?.all_permissions?.includes('delete sales');
        if (!hasDeletePermission) return false;

        // For other roles, check the backend can_edit flag (time-based restrictions)
        if (!sale?.can_edit) return false;

        if (auth.user.role_name !== 'manager') return true;

        if (auth.user.role_name === 'manager') {
            const managerFirms = user.firm_options || [];
            // Sale -> firm_id
            const saleFirmId = sale?.firm_id;
            if (!saleFirmId) return false;
            
            return managerFirms.some(firm => firm.id === saleFirmId);
        }

        return false;
    };

    const canManagerEditExpense = (expense) => {
        if (!auth.user) return false;
        // Directors and superadmins can edit ANY expense regardless of permissions or time restrictions
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;
        // For other roles, check specific permissions first
        const hasEditPermission = auth?.permissions?.all_permissions?.includes('edit expenses');
        if (!hasEditPermission) return false;
        // For other roles, check the backend can_edit flag (time-based restrictions)
        if (!expense?.can_edit) return false;

        if (auth.user.role_name !== 'manager') return true;

        if (auth.user.role_name === 'manager') {
            const managerFirms = user.firm_options || [];
            // Expense -> firm -> id
            const expenseFirmId = expense?.firm?.id;
            if (!expenseFirmId) return false;
            
            return managerFirms.some(firm => firm.id === expenseFirmId);
        }

        return false;
    };

    const canManagerDeleteExpense = (expense) => {
        if (!auth.user) return false;
        // Directors and superadmins can delete ANY expense regardless of permissions or time restrictions
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;
        // For other roles, check specific permissions first
        const hasDeletePermission = auth?.permissions?.all_permissions?.includes('delete expenses');
        if (!hasDeletePermission) return false;
        // For other roles, check the backend can_edit flag (time-based restrictions)
        if (!expense?.can_edit) return false;

        if (auth.user.role_name !== 'manager') return true;

        if (auth.user.role_name === 'manager') {
            const managerFirms = user.firm_options || [];
            // Expense -> firm -> id
            const expenseFirmId = expense?.firm?.id;
            if (!expenseFirmId) return false;
            
            return managerFirms.some(firm => firm.id === expenseFirmId);
        }

        return false;
    };

    const canManagerEditEmployee = (employee) => {
        if (!auth.user) return false;
        // Directors and superadmins can edit ANY employee
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;
        // For other roles, check specific permissions first
        const hasEditPermission = auth?.permissions?.all_permissions?.includes('edit employees');
        if (!hasEditPermission) return false;

        if (auth.user.role_name !== 'manager') return true;

        if (auth.user.role_name === 'manager') {
            const managerFirms = user.firm_options || [];
            // Employee -> firm -> id
            const employeeFirmId = employee?.firm?.id;
            if (!employeeFirmId) return false;
            
            return managerFirms.some(firm => firm.id === employeeFirmId);
        }

        return false;
    };

    const canManagerDeleteEmployee = (employee) => {
        if (!auth.user) return false;
        // Directors and superadmins can delete ANY employee
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;
        // For other roles, check specific permissions first
        const hasDeletePermission = auth?.permissions?.all_permissions?.includes('delete employees');
        if (!hasDeletePermission) return false;

        if (auth.user.role_name !== 'manager') return true;

        if (auth.user.role_name === 'manager') {
            const managerFirms = user.firm_options || [];
            // Employee -> firm -> id
            const employeeFirmId = employee?.firm?.id;
            if (!employeeFirmId) return false;
            
            return managerFirms.some(firm => firm.id === employeeFirmId);
        }

        return false;
    };

    const canManagerEditWarehouse = (warehouseItem) => {
        if (!auth.user) return false;
        // Directors and superadmins can edit ANY warehouse
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;
        // For other roles, check specific permissions first
        const hasEditPermission = auth?.permissions?.all_permissions?.includes('edit warehouses');
        if (!hasEditPermission) return false;

        // Handle manager and accountant with firm-scoped access
        if (auth.user.role_name === 'manager' || auth.user.role_name === 'accountant') {
            // Try to get manager firms from user state first, then warehouse state as fallback
            const managerFirms = user.firm_options || warehouse.available_warehouse_firms || [];
            // Warehouse -> firm -> id
            const warehouseFirmId = warehouseItem?.firm?.id;
            if (!warehouseFirmId) return false;
            
            return managerFirms.some(firm => firm.id === warehouseFirmId);
        }

        // For other roles with permission, allow full access
        return true;
    };

    const canManagerDeleteWarehouse = (warehouseItem) => {
        if (!auth.user) return false;
        // Directors and superadmins can delete ANY warehouse
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;
        // For other roles, check specific permissions first
        const hasDeletePermission = auth?.permissions?.all_permissions?.includes('delete warehouses');
        if (!hasDeletePermission) return false;

        // Handle manager and accountant with firm-scoped access
        if (auth.user.role_name === 'manager' || auth.user.role_name === 'accountant') {
            // Try to get manager firms from user state first, then warehouse state as fallback
            const managerFirms = user.firm_options || warehouse.available_warehouse_firms || [];
            // Warehouse -> firm -> id
            const warehouseFirmId = warehouseItem?.firm?.id;
            if (!warehouseFirmId) return false;
            
            return managerFirms.some(firm => firm.id === warehouseFirmId);
        }

                // For other roles with permission, allow full access
        return true;
    };

    const canManagerEditSparePart = (sparePart) => {
        if (!auth.user) return false;
        // Directors and superadmins can edit ANY spare part
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;
        // For other roles, check specific permissions first
        const hasEditPermission = auth?.permissions?.all_permissions?.includes('edit spare parts');
        if (!hasEditPermission) return false;

        // Handle manager and accountant with firm-scoped access
        if (auth.user.role_name === 'manager' || auth.user.role_name === 'accountant') {
            const managerFirms = user.firm_options || [];
            // Spare part -> firm -> id
            const sparePartFirmId = sparePart?.firm?.id;
            if (!sparePartFirmId) return false;
            
            return managerFirms.some(firm => firm.id === sparePartFirmId);
        }

        // For other roles with permission, allow full access
        return true;
    };

    const canManagerDeleteSparePart = (sparePart) => {
        if (!auth.user) return false;
        // Directors and superadmins can delete ANY spare part
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;
        // For other roles, check specific permissions first
        const hasDeletePermission = auth?.permissions?.all_permissions?.includes('delete spare parts');
        if (!hasDeletePermission) return false;

        // Handle manager and accountant with firm-scoped access
        if (auth.user.role_name === 'manager' || auth.user.role_name === 'accountant') {
            const managerFirms = user.firm_options || [];
            // Spare part -> firm -> id
            const sparePartFirmId = sparePart?.firm?.id;
            if (!sparePartFirmId) return false;
            
            return managerFirms.some(firm => firm.id === sparePartFirmId);
        }

        // For other roles with permission, allow full access
        return true;
    };

    const canManagerEditSparepartExpense = (sparepartExpense) => {
        if (!auth.user) return false;
        // Directors and superadmins can edit ANY spare part expense regardless of time restrictions
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;
        // For other roles, check specific permissions first
        const hasEditPermission = auth?.permissions?.all_permissions?.includes('edit sparepart expenses');
        if (!hasEditPermission) return false;

        // For other roles, check the backend can_edit flag (time-based restrictions)
        if (!sparepartExpense?.can_edit) return false;

        // Handle manager and accountant with firm-scoped access
        if (auth.user.role_name === 'manager' || auth.user.role_name === 'accountant') {
            const managerFirms = user.firm_options || [];
            // Spare part expense -> warehouse -> firm -> id
            const expenseFirmId = sparepartExpense?.warehouse?.firm?.id || sparepartExpense?.firm_id;
            if (!expenseFirmId) return false;
            
            return managerFirms.some(firm => firm.id === expenseFirmId);
        }

        // For other roles with permission, allow full access
        return true;
    };

    const canManagerDeleteSparepartExpense = (sparepartExpense) => {
        if (!auth.user) return false;
        // Directors and superadmins can delete ANY spare part expense regardless of time restrictions
        if (auth.user.role_name === 'superadmin' || auth.user.role_name === 'director') return true;
        // For other roles, check specific permissions first
        const hasDeletePermission = auth?.permissions?.all_permissions?.includes('delete sparepart expenses');
        if (!hasDeletePermission) return false;

        // For other roles, check the backend can_edit flag (time-based restrictions)
        if (!sparepartExpense?.can_edit) return false;

        // Handle manager and accountant with firm-scoped access
        if (auth.user.role_name === 'manager' || auth.user.role_name === 'accountant') {
            const managerFirms = user.firm_options || [];
            // Spare part expense -> warehouse -> firm -> id
            const expenseFirmId = sparepartExpense?.warehouse?.firm?.id || sparepartExpense?.firm_id;
            if (!expenseFirmId) return false;
            
            return managerFirms.some(firm => firm.id === expenseFirmId);
        }

        // For other roles with permission, allow full access
        return true;
    };

    return {
        canManagerEditMaterial,
        canManagerDeleteMaterial,
        canManagerEditProduct,
        canManagerDeleteProduct,
        canManagerEditMaterialImport,
        canManagerDeleteMaterialImport,
        canManagerEditProduction,
        canManagerDeleteProduction,
        canManagerEditSale,
        canManagerDeleteSale,
        canManagerEditExpense,
        canManagerDeleteExpense,
        canManagerEditEmployee,
        canManagerDeleteEmployee,
        canManagerEditWarehouse,
        canManagerDeleteWarehouse,
        canManagerEditSparePart,
        canManagerDeleteSparePart,
        canManagerEditSparepartExpense,
        canManagerDeleteSparepartExpense
    };
}; 